import dayjs from 'dayjs';
import { useEffect, useState, useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { fetchBureauKycConsentTemplate, getOnePagerTemplateData, saveOnePagerTemplateData } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { closeBureauKycConsent, setBureauKycConsentData } from '../../store/investor/action';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';

import { Button, Modal } from '../_custom';
import BureauKycConsentEditor from './BureauKycConsentEditor/BureauKycConsentEditor';
import { sanitizeSlateValue } from '../TextEditor/utils/SlateSerialisationUtil';

import styles from './BureauKycConsent.module.scss';

function BureauKycConsent() {
    const dispatch = useAppDispatch();
    const [savingData, setSavingData] = useState(false);
    const [userInfo, setUserInfo] = useState({});
    const [editorInitialData, setEditorInitialData] = useState<any>([]);
    const [editorReady, setEditorReady] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);

    const companyMainImg = useAppSelector((state) => state.user.domainMetadata?.mainImageUrl);
    const bureauKycConsentData = useAppSelector((state) => state.investor.bureauAndKYCConsentLetter);
    const userData = useAppSelector((state) => state.user.userData);
    const investorOrgId = userData?.investorOrgId;
    const orgId = bureauKycConsentData?.orgId;
    const source = bureauKycConsentData.source;
    const emailId = userData?.primaryEmail;

    useEffect(() => {
        return () => {
            dispatch(closeBureauKycConsent());
        };
    }, []);

    const fetchBureauKycConsentTemplateData = (timeoutRef: { current: NodeJS.Timeout | null }) => {
        setEditorReady(false);
        dispatch(setBureauKycConsentData({ loading: true }));
        getOnePagerTemplateData({
            assesseeOrgId: orgId as string,
            investorOrgId: investorOrgId,
            docType: 'BUREAU_KYC_CONSENT',
        })
            .then(({ onePagerData, templateData }) => {
                if (templateData?.length) {
                    setEditorInitialData(sanitizeSlateValue(templateData));
                    setUserInfo(onePagerData);
                    timeoutRef.current = setTimeout(() => {
                        setEditorReady(true);
                        dispatch(setBureauKycConsentData({ loading: false }));
                    }, 200);
                } else fetchDataForBureauKycConsent();
            })
            .catch(() => {
                fetchDataForBureauKycConsent();
            });
    };

    useEffect(() => {
        const timeoutRef = { current: null as NodeJS.Timeout | null };
        fetchBureauKycConsentTemplateData(timeoutRef);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [orgId]);

    const fetchDataForBureauKycConsent = async (isRegenerate = false) => {
        if (!isRegenerate) {
            setEditorReady(false);
        }
        dispatch(setBureauKycConsentData({ loading: true }));
        dispatch(
            fetchCompanyOverviewData({
                assesseeOrgId: orgId as string,
                investorOrgId: investorOrgId as string,
                onSuccess: async (data: OverviewData, actionCta) => {
                    const bkcTemplate = await fetchBureauKycConsentTemplate({
                        assesseeOrgId: orgId as string,
                        accountType: 'INVESTOR',
                        investorOrgId: investorOrgId as string,
                        primaryEmail: emailId as string,
                    });
                    setEditorInitialData(
                        sanitizeSlateValue(
                            initializeTemplateWithMetadata(bkcTemplate || [], {
                                pnoLogo: companyMainImg as string,
                                companyName: bureauKycConsentData.companyName ?? '',
                                companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                                currentDate: dayjs().format(DATE_FORMAT),
                            }),
                        ),
                    );
                    setEditorReady(true);
                    dispatch(setBureauKycConsentData({ loading: false }));
                },
                onError: () => {
                    setEditorReady(true);
                    dispatch(setBureauKycConsentData({ loading: false }));
                },
            }),
        );
    };

    const saveBureauKycConsent = (editorContent) => {
        if (savingData) return;
        setSavingData(true);
        saveOnePagerTemplateData({
            assesseeOrgId: orgId as string,
            investorOrgId: investorOrgId,
            onePagerData: editorContent || [],
            email: emailId as string,
            docType: 'BUREAU_KYC_CONSENT',
        })
            .then((data) => {
                setTimeout(() => {
                    dispatch(closeBureauKycConsent());
                    setSavingData(false);
                }, 1000);
            })
            .catch(() => {
                setSavingData(false);
            });
    };

    return (
        <div>
            {editorReady && (
                <BureauKycConsentEditor
                    source={source as string}
                    generating={bureauKycConsentData.loading || false}
                    openModal={editorReady}
                    orgId={orgId as string}
                    orgName={bureauKycConsentData.companyName ?? ''}
                    editorInitialData={editorInitialData}
                    editorReady={editorReady}
                    savingData={savingData}
                    saveBureauKycConsent={saveBureauKycConsent}
                    setShowRegenConfirmModal={setShowRegenConfirmModal}
                    userInfo={userInfo}
                />
            )}

            {showRegenConfirmModal && (
                <Modal open={showRegenConfirmModal} onClose={() => setShowRegenConfirmModal(false)} size="flexible">
                    <div className={styles.ConfirmationModal}>
                        <div className={styles.Title}>Regenerate Bureau and KYC Consent Letter</div>
                        <div className={styles.Subtext}>
                            Are you sure you want to regenerate Bureau and KYC Consent Letter? You will lose all the changes made so far.
                        </div>
                        <div className={styles.Buttons}>
                            <Button
                                text={'Cancel'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                }}
                                variant={'purpleinverted'}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text={'Proceed Anyway'}
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForBureauKycConsent(true);
                                }}
                                style={{ marginTop: '0' }}
                                variant={'primary'}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default BureauKycConsent;
